import "./style.scss";
import moment from "moment";
import * as React from "react";
import _ from "lodash";
import Button from "../../../core/Button";
import ContentViewContainer from "../ContentView";
import Feature from "../../../common/components/Feature";
import { GroupBySelect, CompareBySelect, ViewSelect } from "../Dropdown";
import { MetricVisualizationLoadSpin } from "../Spin";
import {
  MapChoropleth,
  BarChart,
  PieChart,
  LineChart,
  AnalyticsBudgetTable
} from "../Visualizations";
import {
  generateQueryString,
  parseQueryString
} from "../../../../modules/common/utils";
import {
  supported_item_metrics,
  supported_flyer_metrics
} from "../../../Analytics/components";
import { sendGAEvent } from "../../../common/google_analytics";
import { copyUrlToClipboard } from "../../../common/utils";
import { Icon, Popover } from "antd";
import {
  group_by_to_data_key_transformation,
  processGroupBy,
  defaultGroupBy,
  defaultView,
  hasFinancePermissions
} from "../../constants";
import { MetricDataEndpointEnum } from "../../actions";
import FullscreenViewContainer from "../FullscreenView";
import { ToplineMetrics } from "../ToplineMetrics";
import { MetricViewProps, MetricViewActions } from "./container";
import { GAContextProps } from "../../../Analytics";

enum RequestOrigin {
  Initial_Load,
  GroupBy_Dropdown,
  CompareBy_Dropdown,
  View_Dropdown
}

interface MetricViewState {
  fullscreen: boolean;
}

class MetricView extends React.Component<
  MetricViewProps & MetricViewActions & RouterChildProps & GAContextProps,
  MetricViewState
> {
  constructor() {
    super();
    this.state = {
      fullscreen: false
    };
    this.brushChange = _.debounce(this.brushChange.bind(this), 500);
  }
  componentWillMount() {
    //Initial load
    let {
      group_by,
      compare_by,
      start_date,
      end_date,
      metric,
      publication_ids,
      merchant_id,
      us_based,
      view
    } = this.props;

    if (
      !(
        !hasFinancePermissions(
          this.props.permissions,
          this.props.merchant_id
        ) && metric == "spend"
      )
    ) {
      if (metric && merchant_id && start_date && end_date) {
        this.props.getToplineMetrics(
          this.props.metric,
          this.props.merchant_id,
          this.props.start_date,
          this.props.end_date
        );
      }

      if (!group_by && !view && !compare_by) {
        view = defaultView(metric);
        group_by = defaultGroupBy(metric, view);
        this.props.setMetricContentView(view);
        this.props.setMetricContentGroupBy(group_by);
      }
      if (
        group_by &&
        start_date &&
        end_date &&
        metric &&
        publication_ids &&
        merchant_id &&
        us_based != null &&
        view != "content"
      ) {
        this.getMetricData(
          group_by,
          compare_by,
          start_date,
          end_date,
          metric,
          publication_ids,
          merchant_id,
          us_based,
          view == "map"
        );
      }
    }
  }

  //TODO add shouldComponentUpdate to limit number of renders

  componentWillReceiveProps(nextProps: any) {
    let {
      group_by,
      compare_by,
      start_date,
      end_date,
      metric,
      publication_ids,
      merchant_id,
      us_based,
      view
    } = nextProps;

    // Refresh when new publications are selected, not if it is spend with no permissions
    if (
      !(
        !hasFinancePermissions(
          this.props.permissions,
          this.props.merchant_id
        ) && metric == "spend"
      )
    ) {
      if (
        _.differenceWith(this.props.publication_ids, publication_ids, _.isEqual)
          .length > 0
      ) {
        if (view != "content")
          this.getMetricData(
            group_by,
            compare_by,
            start_date,
            end_date,
            metric,
            publication_ids,
            merchant_id,
            us_based,
            view == "map"
          );
        this.props.getToplineMetrics(
          nextProps.metric,
          this.props.merchant_id,
          this.props.start_date,
          this.props.end_date
        );
      }

      if (this.props.metric != metric) {
        // Clear Tab selection
        view = defaultView(metric);
        group_by = defaultGroupBy(metric, view);
        this.props.setMetricContentCompareBy(null);
        this.props.setMetricContentGroupBy(group_by);
        this.props.setMetricContentView(view);
        this.getMetricData(
          group_by,
          null,
          start_date,
          end_date,
          metric,
          publication_ids,
          merchant_id,
          us_based,
          view == "map"
        );

        this.props.getToplineMetrics(
          nextProps.metric,
          this.props.merchant_id,
          this.props.start_date,
          this.props.end_date
        );
      }
    }
  }

  getMetricData(
    group_by: string,
    compare_by: string,
    start_date: string,
    end_date: string,
    metric: string,
    publication_ids: number[],
    merchant_id: number,
    us_based: boolean,
    state_level: boolean = false
  ) {
    if (
      group_by &&
      start_date &&
      end_date &&
      metric &&
      publication_ids &&
      merchant_id
    ) {
      if (supported_item_metrics[metric]) {
        this.props.getMetricData(
          MetricDataEndpointEnum.ITEM_METRIC,
          metric,
          merchant_id,
          us_based,
          publication_ids,
          group_by,
          compare_by,
          start_date,
          end_date,
          state_level
        );
      } else if (supported_flyer_metrics[metric]) {
        if (metric == "spend") {
          this.props.getMetricData(
            MetricDataEndpointEnum.SPEND_METRIC,
            metric,
            merchant_id,
            us_based,
            publication_ids,
            group_by,
            compare_by,
            start_date,
            end_date,
            state_level
          );
        } else {
          this.props.getMetricData(
            MetricDataEndpointEnum.FLYER_METRIC,
            metric,
            merchant_id,
            us_based,
            publication_ids,
            group_by,
            compare_by,
            start_date,
            end_date,
            state_level
          );
        }
      }
    }
  }

  updateURL(view: string, group_by: string, compare_by: string) {
    let query_object = parseQueryString(this.props.history.location.search);

    if (group_by) {
      query_object = { ...query_object, group_by: group_by };
    }
    if (compare_by) {
      query_object = { ...query_object, compare_by: compare_by };
    }
    if (view) {
      query_object = { ...query_object, view: view };
    }

    if (!compare_by) delete query_object["compare_by"];
    const queryString = `?${generateQueryString(query_object)}`;
    this.props.history.push(queryString);
  }

  handleGroupbyChange(group_by: string) {
    let {
      compare_by,
      start_date,
      end_date,
      metric,
      publication_ids,
      merchant_id,
      view,
      us_based
    } = this.props;

    this.props.setMetricContentGroupBy(group_by);
    this.props.setMetricContentCompareBy(null);
    this.updateURL(view, group_by, null);

    if (view != "content")
      this.getMetricData(
        group_by,
        compare_by,
        start_date,
        end_date,
        metric,
        publication_ids,
        merchant_id,
        us_based,
        view == "map"
      );
  }

  handleComparebyChange(compare_by: string) {
    let {
      group_by,
      start_date,
      end_date,
      metric,
      publication_ids,
      merchant_id,
      view,
      us_based
    } = this.props;
    this.props.setMetricContentCompareBy(compare_by);
    this.updateURL(view, group_by, compare_by);
    if (view != "content")
      this.getMetricData(
        group_by,
        compare_by,
        start_date,
        end_date,
        metric,
        publication_ids,
        merchant_id,
        us_based,
        view == "map"
      );
  }

  handleViewChange(view: string) {
    let {
      start_date,
      end_date,
      metric,
      publication_ids,
      merchant_id,
      us_based
    } = this.props;
    let group_by = defaultGroupBy(this.props.metric, view, this.props.features);
    this.props.setMetricContentView(view);
    this.props.setMetricContentCompareBy(null);
    this.props.setMetricContentGroupBy(group_by);
    this.updateURL(view, group_by, null);
    if (view != "content")
      this.getMetricData(
        group_by,
        null,
        start_date,
        end_date,
        metric,
        publication_ids,
        merchant_id,
        us_based,
        view == "map"
      );
  }

  renderDropdowns() {
    // page_views - removed
    let comparable_metrics: string[] = [
      "impressions",
      "opens",
      "unique_engaged_visits",
      "clicks",
      "average_time_on_flyer"
    ];
    let dropdowns = [];
    let { group_by, compare_by, metric, view } = this.props;

    if (metric) {
      dropdowns.push(
        <div
          key="view-dropdown"
          className="dropdown-container pure-u-xs-1-3 pure-u-m-1-3 pure-u-l-1-3 pure-u-xl-1-3"
        >
          <h3>1. VIEW DATA AS</h3>
          <ViewSelect
            initial_selection_key={view}
            metric={metric}
            onMenuSelect={this.handleViewChange.bind(this)}
          />
        </div>
      );

      dropdowns.push(
        <div
          key="group-by-dropdown"
          className="dropdown-container pure-u-xs-1-3 pure-u-m-1-3 pure-u-l-1-3 pure-u-xl-1-3"
        >
          <h3>2. GROUP BY</h3>
          <GroupBySelect
            initial_selection_key={`group_by_${group_by}`}
            metric={metric}
            view={view}
            onMenuSelect={this.handleGroupbyChange.bind(this)}
            features={this.props.features}
          />
        </div>
      );

      if (
        comparable_metrics.includes(this.props.metric) &&
        this.props.view != "pie" &&
        this.props.view != "map" &&
        this.props.view != "table" &&
        this.props.view != "content"
      ) {
        dropdowns.push(
          <div
            key="compare-by-dropdown"
            className="dropdown-container pure-u-xs-1-3 pure-u-m-1-3 pure-u-l-1-3 pure-u-xl-1-3"
          >
            <h3>3. COMPARE BY</h3>
            <CompareBySelect
              initial_selection_key={`compare_by_${compare_by}`}
              metric={metric}
              view={view}
              group_by={group_by}
              onMenuSelect={this.handleComparebyChange.bind(this)}
            />
          </div>
        );
      }
    }

    return dropdowns;
  }

  renderVisualization() {
    let visualization = (
      <div className="null-text">
        <span className="analytics-null-text">
          There's no data available for the selected dropdowns.
          <br />
          Try changing the dropdowns to view data.
        </span>
      </div>
    );
    let spinner = <MetricVisualizationLoadSpin />;
    let {
      group_by,
      metric,
      metric_data,
      compare_by,
      view,
      us_based,
      flyer_items_loading,
      metric_data_loading
    } = this.props;

    switch (view) {
      case "bar":
        visualization = metric_data_loading ? (
          spinner
        ) : (
          <BarChart
            group_by={group_by_to_data_key_transformation(
              metric,
              processGroupBy(group_by, us_based)
            )}
            compare_by={compare_by}
            metric={metric}
            data={metric_data}
            filterCompareBy={true}
            initialZoom={{ startIndex: 0, endIndex: 9 }}
            fullscreen={this.state.fullscreen}
            onBrushChange={this.brushChange}
          />
        );
        break;
      case "line":
        visualization = metric_data_loading ? (
          spinner
        ) : (
          <LineChart
            group_by={group_by_to_data_key_transformation(
              metric,
              processGroupBy(group_by, us_based)
            )}
            compare_by={compare_by}
            metric={metric}
            data={metric_data}
            fullscreen={this.state.fullscreen}
            onBrushChange={this.brushChange}
          />
        );
        break;
      case "pie":
        visualization = metric_data_loading ? (
          spinner
        ) : (
          <PieChart
            group_by={group_by_to_data_key_transformation(
              metric,
              processGroupBy(group_by, us_based)
            )}
            metric={metric}
            data={metric_data}
          />
        );
        break;
      case "table":
        visualization = metric_data_loading ? (
          spinner
        ) : (
          <AnalyticsBudgetTable
            gaUserContext={this.props.gaUserContext}
            merchantName={this.props.merchantName}
            data={this.props.metric_data}
          />
        );
        break;
      case "content":
        visualization = <ContentViewContainer />;
        break;
      case "map":
        visualization = metric_data_loading ? (
          spinner
        ) : (
          <MapChoropleth
            data={this.props.metric_data}
            {...this.props}
            group_by={processGroupBy(group_by, us_based, true)}
          />
        );
        break;
    }
    return visualization;
  }

  capitalize = (metric: string) => {
    return _.startCase(metric.replace("_", " "));
  };

  toggleFullscreen = () => {
    if (!this.state.fullscreen) this.fireGABeacon("Fullscreen");
    this.setState(prevState => ({ fullscreen: !prevState.fullscreen }));
  };

  copyLink = () => {
    copyUrlToClipboard();
    this.fireGABeacon("Copy Link");
  };

  brushChange = () => {
    this.fireGABeacon("Graph Brush Interaction");
  };

  fireGABeacon = (eventName: string) => {
    const { merchantName, gaUserContext, gaViewContext } = this.props;
    sendGAEvent("Analytics", eventName, merchantName, {
      ...gaUserContext,
      ...gaViewContext
    });
  };

  render() {
    let { toplineMetrics, analyticMetrics, metric, start_date, end_date } =
      this.props;
    const classes = !this.state.fullscreen
      ? "metric-wrapper"
      : "metric-wrapper fullscreen";

    if (
      !hasFinancePermissions(this.props.permissions, this.props.merchant_id) &&
      metric == "spend"
    ) {
      return null;
    } else {
      analyticMetrics = analyticMetrics ? analyticMetrics : {};
      let thisMetric =
        this.props.metric == "average_time_on_flyer"
          ? "time_on_flyer"
          : this.props.metric;
      let mainMetric =
        analyticMetrics[thisMetric] != null
          ? parseFloat(analyticMetrics[thisMetric].toFixed(2)).toLocaleString()
          : 0;
      if (metric == "spend") mainMetric = `$${mainMetric}`;
      else if (metric == "average_time_on_flyer") mainMetric = `${mainMetric}s`;
      else if (metric == "ttm") metric = "Transfer To Merchant";
      // Since average page views are calculated with Hosted only data, this metric can be unavailable.
      else if (metric == "page_views") mainMetric = "Unavailable";

      return (
        <div className={classes}>
          <div className="MetricContent">
            <div className="topline-wrapper">
              <div
                style={{
                  display: "inline-block",
                  width: "29%",
                  fontSize: 20,
                  marginTop: 5,
                  float: "left"
                }}
              >
                {this.capitalize(metric)}
                <span style={{ fontWeight: "bold", marginLeft: 20 }}>
                  {mainMetric}
                </span>
                <div
                  style={{
                    backgroundColor: "#00C6D7",
                    height: 6,
                    width: 73,
                    marginTop: 5
                  }}
                />
              </div>
              {this.props.topline_metrics_loading ? (
                ""
              ) : (
                <ToplineMetrics
                  start_date={start_date}
                  end_date={end_date}
                  metric={metric}
                  toplineMetrics={toplineMetrics}
                />
              )}
            </div>
            <div className="metrics-dropdown pure-g">
              <div className="pure-g pure-u-xs-20-24 pure-u-m-20-24 pure-u-l-20-24 pure-u-xl-20-24">
                {this.renderDropdowns()}
              </div>
              <div className="metrics-buttons pure-u-xs-4-24 pure-u-m-4-24 pure-u-l-4-24 pure-u-xl-4-24">
                <Feature flag="copy_link">
                  <Button onClick={this.copyLink.bind(this)} type="primary">
                    <Icon type="link" />
                    Share Link
                  </Button>
                </Feature>

                {this.props.group_by != "budget" &&
                  this.props.view != "pie" &&
                  this.props.view != null &&
                  this.props.view != "map" && (
                    <div
                      id="fullscreen-button"
                      title="Full Screen"
                      style={{ marginLeft: 10, cursor: "pointer" }}
                    >
                      <img
                        src="/icon-fullscreen.svg"
                        onClick={this.toggleFullscreen}
                      />
                    </div>
                  )}
              </div>
            </div>
            <div className="metrics-visualization">
              {this.renderVisualization()}
            </div>
          </div>
          {this.state.fullscreen ? (
            <FullscreenViewContainer onClose={this.toggleFullscreen} />
          ) : null}
        </div>
      );
    }
  }
}

export default MetricView as any;



// WEBPACK FOOTER //
// ./client/modules/AnalyticsMetric/components/MetricView/component.tsx